import { Money } from '@klear/money'
import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useKlearAccountTransactions } from '@/hooks/use-klear-account-transactions'
import { DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE } from '@klear/constants'
import { Timestamp } from '@klear/time'
import { useMemo } from 'react'

interface BalanceHeaderProps {
  historyWindowDays?: number
}

export default function BalanceHeader({
  historyWindowDays = DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE
}: BalanceHeaderProps) {
  const { klearFinancialAccount } = useKlearFinancialAccount()
  const { transactions = [] } = useKlearAccountTransactions(historyWindowDays)

  const filteredTransactions = useMemo(() => {
    const settled = transactions.filter((transaction) => transaction.isSettled)
    if (historyWindowDays === 0) {
      return settled
    }

    const cutoffDate = Timestamp.now().subtract(historyWindowDays, 'day')
    return settled.filter((transaction) => transaction.createdAt.isSameOrAfter(cutoffDate))
  }, [transactions, historyWindowDays])

  const currency = klearFinancialAccount?.balanceCurrency ?? DEFAULT_CURRENCY_CODE

  let totalInbound = Money.zero(currency)
  let totalOutbound = Money.zero(currency)
  filteredTransactions.forEach((transaction) => {
    if (transaction.isOutbound) {
      totalOutbound = totalOutbound.add(transaction.amount)
    } else {
      totalInbound = totalInbound.add(transaction.amount)
    }
  })

  return (
    <>
      <div className="text-3xl font-bold">
        {(klearFinancialAccount?.account ? klearFinancialAccount.balanceCash : Money.zero(currency)).toString()}
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="flex items-center text-emerald-500">
          <ArrowUpIcon className="mr-1 h-3 w-3" />
          {totalInbound.toString()}
        </span>
        <span className="flex items-center text-red-500">
          <ArrowDownIcon className="mr-1 h-3 w-3" />
          {totalOutbound.toString()}
        </span>
      </div>
    </>
  )
}
